import { StyleSheet } from "react-native-unistyles";
import { Text, View } from "@src/components";
import { PaginationNavButton } from "@visioni/a3s-ui/components";
import type { CursorPager } from "@visioni/a3s-monitoring/app";

interface CursorPagerBarProps {
  readonly pager: CursorPager;
  readonly nextCursor: string | null;
  readonly isFetching: boolean;
  /** What the page on screen holds, e.g. `25 activations`. */
  readonly summary: string;
}

/**
 * Not the shared `Pagination`: that needs a total, and the replica pages by
 * forward-only cursor with no count. Same bar, prev/next only.
 */
export function CursorPagerBar({ pager, nextCursor, isFetching, summary }: CursorPagerBarProps) {
  return (
    <View style={styles.pagerBar}>
      <Text textStyle="sm/regular" color="textMuted">
        Page {pager.page} · {summary}
      </Text>
      <View flexDirection="row" gap="sm">
        <PaginationNavButton
          direction="previous"
          disabled={!pager.canGoBack || isFetching}
          onPress={pager.back}
          accessibilityLabel="Previous page"
        />
        <PaginationNavButton
          direction="next"
          // Enabled by the cursor, never by the row count: DynamoDB's limit
          // bounds the read rather than the result, so a short page can
          // still have more behind it.
          disabled={nextCursor === null || isFetching}
          onPress={() => {
            if (nextCursor !== null) pager.next(nextCursor);
          }}
          accessibilityLabel="Next page"
        />
      </View>
    </View>
  );
}

/** `1 signal`, `25 signals`. */
export function countOf(n: number, noun: string): string {
  return n === 1 ? `1 ${noun}` : `${String(n)} ${noun}s`;
}

const styles = StyleSheet.create((theme) => ({
  // Matches `Pagination`'s `bar` variant.
  pagerBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing.xl,
    marginHorizontal: theme.spacing.xl,
    marginBottom: theme.spacing.xl,
    paddingVertical: theme.spacing.xl,
    paddingHorizontal: theme.spacing["6xl"],
    backgroundColor: theme.colors.surface2.background,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.xl,
  },
}));
